import { useCallback, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  hasTableStateInSearch,
  paginationFromTableSearch,
  searchFromPagination
} from "utils/helpers/tableUrlSync";

/**
 * Keeps list table pagination/filters in sync with URL query params.
 * URL is the source of truth on initial load; subsequent interactions update the URL first.
 *
 * @param {((pagination: object) => void)|null} onPaginationFromUrl - refetch when URL carries table state (back/forward or return from detail)
 */
function useTableUrlSync(onPaginationFromUrl = null) {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const hasUrlTableState = hasTableStateInSearch(search);
  const urlMergeConsumedRef = useRef(false);
  const skipPopHandlerRef = useRef(false);
  const skipUrlPaginationRef = useRef(false);

  useEffect(() => {
    urlMergeConsumedRef.current = false;
  }, [pathname, search]);

  const mergePaginationFromUrl = useCallback(
    (pagination) => {
      if (!hasUrlTableState || urlMergeConsumedRef.current) {
        return pagination;
      }
      urlMergeConsumedRef.current = true;
      return paginationFromTableSearch(search, pagination);
    },
    [hasUrlTableState, search]
  );

  const syncPaginationToUrl = useCallback(
    (pagination) => {
      const nextSearch = searchFromPagination(pagination, search);
      if (nextSearch !== search) {
        skipPopHandlerRef.current = true;
        skipUrlPaginationRef.current = true;
        history.replace({ pathname, search: nextSearch });
      }
    },
    [history, pathname, search]
  );

  useEffect(() => {
    if (skipUrlPaginationRef.current) {
      skipUrlPaginationRef.current = false;
      return;
    }
    if (!onPaginationFromUrl || !hasTableStateInSearch(search)) {
      return;
    }
    onPaginationFromUrl(paginationFromTableSearch(search, {}));
  }, [search, pathname, onPaginationFromUrl]);

  useEffect(() => {
    if (!onPaginationFromUrl) {
      return undefined;
    }

    const unlisten = history.listen((location, action) => {
      if (skipPopHandlerRef.current) {
        skipPopHandlerRef.current = false;
        return;
      }
      if (location.pathname !== pathname) {
        return;
      }
      if (!hasTableStateInSearch(location.search)) {
        return;
      }
      if (action === "POP" || action === "PUSH" || action === "REPLACE") {
        onPaginationFromUrl(paginationFromTableSearch(location.search, {}));
      }
    });

    return unlisten;
  }, [history, onPaginationFromUrl, pathname]);

  return {
    hasUrlTableState,
    mergePaginationFromUrl,
    syncPaginationToUrl
  };
}

export default useTableUrlSync;
